import { FC, useEffect, useRef } from 'react';

import { FullPageLoading } from '../../../components/loading/FullPageLoading';
import { useBasketOptional } from '../../../pages/manual-booking/useBasket';
import { useAuthenticatedViewer } from '../../../providers/ViewerProvider';

/**
 * Terminal sign-out page: it starts the AuthKit logout redirect and then deliberately does
 * nothing else — no viewer-state clear, no `navigate()`.
 *
 * Both of those used to happen here, and both were wasted work: `logoutUser()` always ends in a
 * full-page navigation to the AuthKit logout URL, so whatever route we rendered in the meantime
 * was thrown away milliseconds later. Worse, throwing it away cancelled that route's lazy-chunk
 * request, which crashed the app into the "Something went wrong" screen for the one to three
 * seconds the WorkOS round-trip took. Clearing the viewer state is what pushed the route guards
 * into rendering `/login` in the first place — and `/login` immediately fires its own sign-in
 * redirect, which can beat the logout redirect and sign the user straight back in. Sitting
 * still on a spinner avoids both.
 */
const Logout: FC = () => {
  const { logoutUser } = useAuthenticatedViewer();
  const basketContext = useBasketOptional();
  const signOutStarted = useRef(false);

  useEffect(() => {
    if (signOutStarted.current) {
      return;
    }
    signOutStarted.current = true;

    // Clear the persisted basket so it doesn't carry over to the next user on this browser.
    basketContext?.clearBasket();
    logoutUser();
  }, [basketContext, logoutUser]);

  return <FullPageLoading />;
};

export default Logout;
